"use client";

import { useMemo, useState } from "react";
import { DetailBody, Modal } from "./BattleDemoParts";
import { CharView, DemoResponse, ItemView } from "./battleDemoTypes";
import { strengthToCarry } from "./characterViews";

type ShopData = NonNullable<DemoResponse["shop"]>;
type Pane = "items" | "store";

/** 購入・修理フェーズ。キャラ毎に売買・修理・装備の付け替えを行う */
export default function BattleDemoShop({
  shop, busy, onAction, onLeave, onAbandon,
}: {
  shop: ShopData;
  busy: boolean;
  onAction: (op: string, args: Record<string, unknown>) => Promise<void>;
  onLeave: () => void;
  onAbandon: () => void;
}) {
  const [charId, setCharId] = useState(shop.party[0]?.id ?? "");
  const [pane, setPane] = useState<Pane>("items");
  const [group, setGroup] = useState("道具");
  const [equipType, setEquipType] = useState("");
  const [showDetail, setShowDetail] = useState(false);
  const char: CharView | undefined = shop.party.find((c) => c.id === charId) ?? shop.party[0];

  const groups = useMemo(() => [...new Set(shop.stock.map((s) => s.group))], [shop.stock]);
  const types = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of shop.stock) if (s.group === group && s.equip_type) m.set(s.equip_type, s.equip_label ?? s.equip_type);
    return [...m.entries()];
  }, [shop.stock, group]);
  const stock = shop.stock.filter((s) => s.group === group && (!equipType || s.equip_type === equipType));

  if (!char) return null;
  const itemName = (id: string) => char.items.find((i) => i.id === id)?.name ?? "";
  const act = (op: string, args: Record<string, unknown>) => onAction(op, { char: char.id, ...args });
  const repairAll = char.items.filter((i) => i.equipped && i.repair > 0);

  return (
    <div className="bd-app bd-page">
      <header className="bd-page-head">
        <h1>購入・修理<small>次は {shop.next_stage} 回目</small></h1>
        <button className="bd-icon-btn" onClick={onAbandon}>終える</button>
      </header>

      <div className="bd-tabs">
        {shop.party.map((c) => (
          <button key={c.id} className={`bd-tab ${c.id === char.id ? "bd-tab-on" : ""}`} onClick={() => setCharId(c.id)}>{c.name}</button>
        ))}
      </div>

      <section className="bd-card">
        <div className="bd-who">
          <strong>{char.name}<small className="bd-dim"> {char.title}</small></strong>
          <span className="bd-money">{char.money} CC</span>
        </div>
        <div className="bd-who">
          <span className="bd-dim">HP {char.hp.max}{char.pools.map((p) => `・${p.type.toUpperCase()} ${p.max}`).join("")}・積載 {char.load.current}/{char.load.max}</span>
          {char.detail && <button className="bd-btn bd-btn-small" onClick={() => setShowDetail(true)}>詳細</button>}
        </div>
      </section>

      <div className="bd-tabs">
        <button className={`bd-tab ${pane === "items" ? "bd-tab-on" : ""}`} onClick={() => setPane("items")}>持ち物と装備</button>
        <button className={`bd-tab ${pane === "store" ? "bd-tab-on" : ""}`} onClick={() => setPane("store")}>店</button>
      </div>

      {pane === "items" && (
        <>
          <section className="bd-card">
            <h3>装備部位</h3>
            {char.equipment.map((part) => {
              const ids = [...new Set(part.layers.flatMap((l) => l.items))];
              return <div key={part.name} className="bd-part"><span className="bd-dim">{part.name}</span><span>{ids.length ? ids.map(itemName).join("・") : "—"}</span></div>;
            })}
            {repairAll.length > 0 && (
              <div className="bd-item-ops">
                <button className="bd-btn bd-btn-small" disabled={busy} onClick={async () => { for (const i of repairAll) await act("repair", { item: i.id }); }}>
                  着けている物を全て修理（{repairAll.reduce((a, i) => a + i.repair, 0)} CC）
                </button>
              </div>
            )}
          </section>
          <section className="bd-card">
            <h3>持ち物</h3>
            {char.items.length === 0 && <div className="bd-note">何も持っていない。</div>}
            {char.items.map((i) => <ItemRow key={i.id} item={i} busy={busy} party={shop.party} charId={char.id} act={act} />)}
          </section>
        </>
      )}

      {pane === "store" && (
        <section className="bd-card">
          <div className="bd-tabs">
            {groups.map((g) => (
              <button key={g} className={`bd-tab ${g === group ? "bd-tab-on" : ""}`} onClick={() => { setGroup(g); setEquipType(""); }}>{g}</button>
            ))}
          </div>
          {types.length > 1 && (
            <select className="bd-select" value={equipType} onChange={(e) => setEquipType(e.target.value)}>
              <option value="">装備種：全て</option>
              {types.map(([t, label]) => <option key={t} value={t}>{label}</option>)}
            </select>
          )}
          {stock.map((s) => (
            <div key={s.slug} className="bd-item">
              <div className="bd-item-main">
                <div className="bd-item-head">
                  <strong>{s.name}</strong>
                  <span style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <button className="bd-btn bd-btn-small" disabled={busy || char.money < s.price} onClick={() => act("buy", { slug: s.slug, count: 1 })}>{s.price} CC</button>
                    {s.stackable && <button className="bd-btn bd-btn-small" disabled={busy || char.money < s.price * 10} onClick={() => act("buy", { slug: s.slug, count: 10 })}>×10 {s.price * 10} CC</button>}
                  </span>
                </div>
                <span className="bd-dim">
                  {s.summary}{s.durability ? `・耐久 ${s.durability}` : ""}・容量 {s.bulk}
                  {strengthToCarry(s.bulk) > 0 && <span className={(char.base.str ?? 0) < strengthToCarry(s.bulk) ? "bd-short" : ""}>・要る筋力 {strengthToCarry(s.bulk)}</span>}
                </span>
              </div>
            </div>
          ))}
        </section>
      )}

      <div className="bd-start-bar">
        <button className="bd-btn bd-btn-primary" disabled={busy} onClick={onLeave}>次の戦闘へ</button>
      </div>

      {showDetail && char.detail && <Modal onClose={() => setShowDetail(false)}><DetailBody detail={char.detail} /></Modal>}
    </div>
  );
}

function ItemRow({
  item, busy, party, charId, act,
}: {
  item: ItemView;
  busy: boolean;
  party: CharView[];
  charId: string;
  act: (op: string, args: Record<string, unknown>) => Promise<void>;
}) {
  const [giving, setGiving] = useState(false);
  const d = item.durability;
  const need = strengthToCarry(item.bulk);
  const weak = need > 0 && (party.find((c) => c.id === charId)?.base.str ?? 0) < need;
  return (
    <div className="bd-item">
      <div className="bd-item-main">
        <div className="bd-item-head">
          <strong>{item.name}{item.count > 1 ? ` ×${item.count}` : ""}{item.equipped && <span className="bd-tag">装備中</span>}</strong>
          {d && d.max < 9000 && <span className="bd-note">耐久 {d.current}/{d.max}</span>}
        </div>
        <span className="bd-dim">{item.summary}{need > 0 && <span className={weak ? "bd-short" : ""}>・要る筋力 {need}</span>}</span>
      </div>
      <div className="bd-item-ops">
        {item.equip_type && !item.equipped && <button className="bd-btn bd-btn-small" disabled={busy || weak} onClick={() => act("equip", { item: item.id })}>{weak ? "筋力不足" : "着ける"}</button>}
        {item.equip_type && item.equipped && !item.locked && <button className="bd-btn bd-btn-small" disabled={busy} onClick={() => act("unequip", { item: item.id })}>外す</button>}
        {item.repair > 0 && <button className="bd-btn bd-btn-small" disabled={busy} onClick={() => act("repair", { item: item.id })}>修理 {item.repair}</button>}
        {!item.locked && <button className="bd-btn bd-btn-small" disabled={busy} onClick={() => act("sell", { item: item.id, count: 1 })}>売る {item.sell}</button>}
        {!item.locked && party.length > 1 && <button className="bd-btn bd-btn-small" disabled={busy} onClick={() => setGiving(!giving)}>渡す</button>}
      </div>
      {giving && (
        <div className="bd-item-ops">
          {party.filter((c) => c.id !== charId).map((c) => (
            <button key={c.id} className="bd-btn bd-btn-small" disabled={busy} onClick={() => { act("give", { item: item.id, to: c.id }); setGiving(false); }}>{c.name}へ</button>
          ))}
        </div>
      )}
    </div>
  );
}
